import { GetPaymentHistoryInput, PaymentHistory } from "@/types/payment-history.type";
import { Constants } from "@/services/web/api.const";
import { useMemo } from "react";
import { PaginationResult } from "@/types/pagination-result.type";
import { formatRangeDate } from "@/helper/date-time";
import { snakeCaseToCamelCase } from "@/helper/snake-case-to-camel-case";
import { useApi } from "@/services/web/use-api";
import { addQueryParametersToUrl } from "@/helper/query-params/add-query-params-to-url.util";
import { auth0Config } from "@/configs/auth-config";
import { READ_ORDERS_SCOPE } from "@/modules/finance/helper/constants/api-payment-history.const";

export const usePaymentHistory = ({
  cityId,
  pagination,
  filter,
  sortOrders,
}: GetPaymentHistoryInput): {
  paymentHistoryData?: PaginationResult<PaymentHistory>;
  totalPage?: number;
  loading?: boolean;
  error?: Error;
} => {
  const url = useMemo(() => {
    const url = `${Constants.CLIENTS_API_URL}/orders`;

    let params: Record<string, unknown> = {};

    if (pagination) {
      params = {
        ...params,
        page_index: pagination.pageIndex,
        page_size: pagination.pageSize,
      };
    }

    if (filter) {
      const { startDate: start_date, endDate: end_date } = formatRangeDate(filter.startDate, filter.endDate);

      params = {
        ...params,
        start_date,
        end_date,
        ...(filter.paymentMethod && { paymentMethod: filter.paymentMethod }),
        ...(filter.licensePlate && { licensePlate: filter.licensePlate }),
        ...(filter.zoneName && { zoneName: filter.zoneName }),
        ...(filter.userProfile && { userProfile: filter.userProfile }),
      };
    }

    if (sortOrders) {
      const { sortField, sortOrder } = sortOrders;

      params = {
        ...params,
        sort_field: snakeCaseToCamelCase(sortField),
        sort_order: sortOrder.toLowerCase(),
      };
    }

    if (cityId) {
      params = {
        ...params,
        cityId,
      };
    }

    return addQueryParametersToUrl(url, params);
  }, [cityId, pagination, filter, sortOrders]);

  const {
    isLoading: loading,
    error,
    data,
  } = useApi<PaginationResult<PaymentHistory>>(url, {
    method: "GET",
    audience: auth0Config.clientAudiance,
    scope: READ_ORDERS_SCOPE,
  });

  return {
    paymentHistoryData: data,
    totalPage: data?.total_pages,
    loading,
    error: error ?? undefined,
  };
};
